import { formatAddress, formatDate, formatEin, formatPhone, titleCase } from '../../Bgv/BusinessDetailReport/businessReportModel'
import {
    ADDRESS_TYPES, BUSINESS_CODES, BUSINESS_GROUPS, FOLLOW_UPS, OWNER_CODES, OWNER_GROUPS, PHONE_SOURCES, SCORE_WORDS, SOURCES,
    describeDobMatch, describeLink, describeSummary,
} from './verificationCodes'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const norm = (value) => text(value).toUpperCase().replace(/[^A-Z0-9]/g, '')
const digits = (value) => text(value).replace(/\D/g, '')
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const pieces = (...parts) => parts.filter(Boolean).join(' · ')
const scoreOf = (value) => (value == null || value === '' || isNaN(Number(value)) ? null : Math.round(Number(value) / 10) * 10)
const personName = (name) => titleCase([name?.First, name?.Middle, name?.Last, name?.Suffix].filter(Boolean).join(' '))
const TONE_RANK = { declined: 3, watch: 2, neutral: 1, approved: 0, notSent: 0 }
const worst = (tones) => tones.reduce((a, b) => (TONE_RANK[b] > TONE_RANK[a] ? b : a), 'approved')

export const scoreText = (score) => (score == null ? 'No score' : `${SCORE_WORDS[score] || 'Score'} · ${score} of 50`)

export function businessTone(score, watchlist) {
    if (score == null) return 'neutral'
    if (score >= 40) return 'approved'
    if (score >= 20) return 'watch'
    if (score === 10) return watchlist ? 'declined' : 'watch'
    return 'declined'
}

export function ownerTone(score, ssnSent, dobSent) {
    if (score == null) return 'neutral'
    if (score >= 40) return 'approved'
    if (score >= 20) return 'watch'
    return ssnSent && dobSent ? 'declined' : 'watch'
}

function notes(risks, table, groups) {
    const rows = list(risks).filter(r => r && (r.RiskCode || r.Description)).map(r => {
        const code = text(r.RiskCode)
        const known = table[code] || table[String(Number(code))]
        return known ? { code, ...known } : { code, group: groups[1], tone: 'neutral', label: text(r.Description) || `Lexis code ${code}` }
    })
    return groups.map(group => ({ group, items: rows.filter(r => r.group === group) })).filter(g => g.items.length)
}

const allNotes = (grouped) => grouped.flatMap(g => g.items)

function watchHit(hit, who) {
    const entity = titleCase(hit.EntityName || hit.CompanyName || personName(hit.Name))
    return {
        who,
        title: entity || 'Listed party',
        list: text(hit.Table),
        program: text(hit.Program),
        country: titleCase(hit.Country),
        address: [formatAddress(hit.Address), titleCase(hit.Country)].filter(Boolean).join(', '),
    }
}

function sourceLine(summary) {
    const description = text(summary.Description)
    const confirmed = /no business inputs verified/i.test(description) || Number(summary.Index) === 0
        ? '' : description.replace(/^input business\s*/i, '').replace(/\s*verified\.?$/i, '').toLowerCase().replace(/\bfein\b/g, 'EIN')
    return {
        label: SOURCES[text(summary.Type).toUpperCase()] || titleCase(summary.Type),
        confirmed: confirmed ? `${confirmed.charAt(0).toUpperCase()}${confirmed.slice(1)}` : 'Nothing confirmed',
        count: Number(summary.Index) || 0,
    }
}

function buildBusiness(result) {
    const company = result.CompanyResults || {}
    const sent = result.InputEcho?.Company || {}
    const best = company.BestInformation || {}
    const verified = company.VerifiedInput || {}
    const ind = company.VerificationIndicators || {}
    const ofac = list(company.OFACWatchLists?.OFACWatchList).filter(Boolean)

    const name = titleCase(best.CompanyName || verified.CompanyName || sent.CompanyName)
    const sentName = titleCase(sent.CompanyName)
    const score = scoreOf(company.BusinessVerification?.Index)
    const grouped = notes(company.RiskIndicators?.RiskIndicator, BUSINESS_CODES, BUSINESS_GROUPS)
    const codes = new Set(allNotes(grouped).map(n => String(Number(n.code))))

    const detail = (label, key, sentValue, shown) => {
        const isSent = Boolean(text(sentValue))
        const status = text(ind[key]) === '1' ? 'verified' : isSent ? 'notVerified' : 'notSent'
        return { label, status, value: shown || '' }
    }
    const details = [
        detail('Name', 'CompanyName', sent.CompanyName, sentName),
        detail('Street', 'StreetAddress', sent.Address?.StreetAddress1 || sent.Address?.StreetName, titleCase(sent.Address?.StreetAddress1)),
        detail('City', 'City', sent.Address?.City, titleCase(sent.Address?.City)),
        detail('State', 'State', sent.Address?.State, text(sent.Address?.State)),
        detail('ZIP', 'Zip', sent.Address?.Zip5, text(sent.Address?.Zip5)),
        detail('Phone', 'Phone', sent.Phone, formatPhone(sent.Phone)),
        detail('EIN', 'FEIN', sent.FEIN, formatEin(sent.FEIN)),
    ]
    const verifiedCount = details.filter(d => d.status === 'verified').length

    const compareRow = (label, a, b, same) => (a || b ? { label, sent: a || 'Not sent', lexis: b || 'Not on file', differs: Boolean(a && b && !same) } : null)
    const compare = Object.keys(best).length ? [
        compareRow('Name', sentName, titleCase(best.CompanyName), norm(sent.CompanyName) === norm(best.CompanyName)
            || norm(sent.AlternateCompanyName) === norm(best.CompanyName)),
        compareRow('Address', formatAddress(sent.Address), formatAddress(best.Address),
            norm(formatAddress(sent.Address)) === norm(formatAddress(best.Address))
            || (norm(sent.Address?.Zip5) === norm(best.Address?.Zip5) && digits(sent.Address?.StreetAddress1).slice(0, 6) === digits(best.Address?.StreetAddress1).slice(0, 6))),
        compareRow('Phone', formatPhone(sent.Phone), formatPhone(best.Phone), digits(sent.Phone) === digits(best.Phone)),
        compareRow('EIN', formatEin(sent.FEIN), formatEin(best.FEIN), digits(sent.FEIN) === digits(best.FEIN)),
    ].filter(Boolean) : []

    const residential = list(company.ResidentialBusinesses?.ResidentialBusiness)[0]
    const addressType = residential ? (ADDRESS_TYPES[Number(residential.Indicator)] || titleCase(residential.Description)) : ''

    const standing = codes.has('13') ? 'Not in good standing' : codes.has('14') ? 'Inactive with the state'
        : codes.has('15') ? 'Not registered in its state' : codes.has('12') ? 'Bankruptcy suggests it may be closed' : ''
    const age = codes.has('23') ? 'First reported under 12 months ago' : codes.has('24') ? 'First reported under 24 months ago' : ''

    const others = (items, field) => list(items).filter(i => i && !(field && norm(i.CompanyName) === norm(best.CompanyName || sent.CompanyName)))
    const more = [
        { label: 'Alternate name sent', value: titleCase(sent.AlternateCompanyName) },
        { label: 'Other businesses on the same EIN', value: others(company.FEINMatchResults?.FEINMatchResult, true)
            .map(i => [titleCase(i.CompanyName), formatAddress(i.Address)].filter(Boolean).join(', ')).join('; ') },
        { label: 'Businesses on the same phone', value: list(company.NamesAddressesOfPhones?.NamesAddressesOfPhone).filter(Boolean)
            .map(i => [titleCase(i.CompanyName), formatAddress(i.Address)].filter(Boolean).join(', ')).join('; ') },
        { label: 'Phones for this name and address', value: list(company.PhonesOfNameAddresses?.PhonesOfNameAddress).filter(Boolean)
            .map(p => formatPhone(p.PhoneOfNameAddress)).filter(Boolean).join('; ') },
        { label: 'Mail drop address', value: company.AddressRisk?.AddressIsCMRA === true ? 'Yes' : company.AddressRisk?.AddressIsCMRA === false ? 'No' : '' },
        { label: 'Lexis says', value: text(company.BusinessVerification?.Description) },
    ].filter(d => d.value)

    return {
        name: name || 'Business name not returned',
        sentAs: sentName && norm(sentName) !== norm(name) && norm(sent.AlternateCompanyName) !== norm(best.CompanyName) ? sentName : '',
        address: formatAddress(best.Address || verified.Address || sent.Address),
        phone: formatPhone(best.Phone || sent.Phone),
        ein: formatEin(best.FEIN || sent.FEIN),
        addressType,
        mailDrop: company.AddressRisk?.AddressIsCMRA === true,
        score,
        scoreTone: businessTone(score, ofac.length > 0),
        details,
        verifiedCount,
        compare,
        notes: grouped,
        sources: list(company.VerificationSummaries?.VerificationSummary).filter(Boolean).map(sourceLine),
        standing,
        age,
        ofac: ofac.map(h => watchHit(h, 'Business')),
        links: list(company.BusinessToAuthorizedRepLinkIndexes?.BusinessToAuthorizedRepLinkIndex).filter(Boolean),
        more,
    }
}

function buildOwner(rep, i, inputs, links) {
    const echo = rep.InputEcho || {}
    const input = inputs[i] || {}
    const sequence = text(echo.Sequence) || String(i + 1)
    const missing = new Set(list(rep.RiskIndicators?.RiskIndicator).map(r => text(r?.RiskCode)))
    const ssnSent = Boolean(text(echo.SSN) || text(input.SSN)) && !missing.has('79')
    const dobSent = Boolean(echo.DOB?.Year || input.DOB?.Year) && !missing.has('81')
    const phoneSent = Boolean(text(echo.Phone) || text(input.Phone)) && !missing.has('80')

    const current = personName(rep.CurrentName)
    const sentName = personName(echo.Name?.First ? echo.Name : input.Name)
    const name = current || sentName || `Owner ${sequence}`
    const score = scoreOf(rep.ComprehensiveVerificationIndex)
    const grouped = notes(rep.RiskIndicators?.RiskIndicator, OWNER_CODES, OWNER_GROUPS)
    const codes = new Set(allNotes(grouped).map(n => n.code))
    const watch = list(rep.WatchLists?.WatchList).filter(Boolean)
    const deceased = codes.has('DI') || Boolean(rep.DecedentInfo && Object.keys(rep.DecedentInfo).length)
    const link = describeLink(links.find(l => text(l.InputRepNumber) === sequence) || links[i])

    const nas = describeSummary(rep.NameAddressSSNSummary, 'SSN')
    const nap = describeSummary(rep.NameAddressPhoneSummary, 'phone')
    const matches = [
        { label: 'Name, address and SSN', ...(!ssnSent
            ? { text: nas && Number(rep.NameAddressSSNSummary) ? `${nas.text}, SSN not sent` : 'SSN not sent', tone: 'notSent' }
            : nas || { text: 'Not returned', tone: 'neutral' }) },
        { label: 'Name, address and phone', ...(!phoneSent ? { text: 'Phone not sent', tone: 'notSent' } : nap || { text: 'Not returned', tone: 'neutral' }),
            source: PHONE_SOURCES[text(rep.NameAddressPhoneType)] },
        { label: 'Date of birth', ...describeDobMatch(rep.DOBMatchLevel, dobSent) },
        ssnSent && rep.SSNFoundForLexID != null && { label: 'SSN on file for this person', text: rep.SSNFoundForLexID ? 'Yes' : 'No', tone: rep.SSNFoundForLexID ? 'approved' : 'watch' },
    ].filter(Boolean)

    const corrected = Object.entries(rep.InputCorrected || {}).filter(([, v]) => v && (typeof v !== 'object' || Object.keys(v).length)).map(([k]) => (
        { Name: 'name', Address: 'address', SSN: 'SSN', Phone: 'phone', DOB: 'date of birth' }[k] || k.toLowerCase()))
    const flags = [
        watch.length && { label: 'OFAC', tone: 'declined' },
        deceased && { label: 'Deceased', tone: 'declined' },
        link?.tone === 'watch' && link.index === 0 && { label: 'Cannot be linked', tone: 'watch' },
        rep.AddressRisk?.AddressIsPOBox && { label: 'PO box', tone: 'watch' },
        rep.AddressRisk?.AddressIsCMRA && { label: 'Mail drop', tone: 'watch' },
        !ssnSent && { label: 'SSN not sent', tone: 'notSent' },
        text(rep.AreaCodeSplitFlag).toUpperCase() === 'Y' && { label: 'Area code changing', tone: 'neutral' },
        corrected.length && { label: `Lexis corrected the ${corrected.join(', ')}`, tone: 'neutral' },
    ].filter(Boolean)

    const followUps = list(rep.PotentialFollowupActions?.PotentialFollowupAction).filter(Boolean)
        .map(f => FOLLOW_UPS[text(f.RiskCode).toUpperCase()] || text(f.Description)).filter(Boolean)

    const history = list(rep.ChronologyHistories?.ChronologyHistory).filter(Boolean).map(h => ({
        address: formatAddress(h.Address),
        firstSeen: formatDate(h.DateFirstSeen),
        lastSeen: formatDate(h.DateLastSeen),
        best: h.IsBestAddress === true,
        phone: formatPhone(h.Phone),
    })).filter(h => h.address)

    const ssnInfo = rep.SSNInfo || {}
    const issued = [ssnInfo.IssuedStartDate?.Year, ssnInfo.IssuedEndDate?.Year].filter(Boolean)
    const more = [
        { label: 'Name sent', value: sentName && norm(sentName) !== norm(current) ? sentName : '' },
        { label: 'Other names', value: list(rep.AKAs?.AKA).filter(Boolean).map(a => [personName(a.Name), a.DateLastSeen?.Year && `last seen ${formatDate(a.DateLastSeen)}`]
            .filter(Boolean).join(', ')).join('; ') },
        { label: 'SSN issued', value: issued.length ? [...new Set(issued)].join('-') : '' },
        { label: 'SSN issued in', value: titleCase(ssnInfo.IssuedStateName) || text(ssnInfo.IssuedState) },
        { label: 'Address sent', value: formatAddress(echo.Address || input.Address) },
        { label: 'Phone sent', value: formatPhone(echo.Phone || input.Phone) },
        { label: "Driver's license state sent", value: text(echo.DriverLicenseState || input.DriverLicenseState) },
        { label: 'Phone sent is listed to', value: [personName(rep.ReversePhone?.Name), formatAddress(rep.ReversePhone?.Address)].filter(Boolean).join(', ') },
        { label: 'Phone for this name and address', value: formatPhone(rep.PhoneOfNameAddress) },
        { label: 'Address of the phone sent', value: formatAddress(rep.PhoneAddress) },
        { label: 'Phone checked against', value: PHONE_SOURCES[text(rep.NameAddressPhoneType)] || '' },
        { label: 'Phone status', value: { C: 'Connected', D: 'Disconnected' }[text(rep.NameAddressPhoneStatus)] || '' },
        { label: 'New area code', value: rep.NewAreaCode?.AreaCode ? [rep.NewAreaCode.AreaCode, rep.NewAreaCode.EffectiveDate && `from ${formatDate(rep.NewAreaCode.EffectiveDate)}`].filter(Boolean).join(' ') : '' },
        { label: 'Date of death', value: formatDate(rep.DecedentInfo?.DOD) },
    ].filter(d => d.value)

    const tone = ownerTone(score, ssnSent, dobSent)
    const noteTones = allNotes(grouped).map(n => n.tone)
    const cardTone = worst([tone, ...noteTones.filter(t => t !== 'notSent'), ...flags.map(f => f.tone).filter(t => t !== 'notSent'), link?.tone === 'watch' ? 'watch' : 'approved'])

    return {
        key: `owner-${sequence}-${i}`,
        name,
        firstName: name.split(' ')[0],
        sentAs: sentName && current && norm(sentName) !== norm(current) ? sentName : '',
        score,
        tone,
        capped: !ssnSent,
        ssnSent,
        dobSent,
        link,
        matches,
        notes: grouped,
        flags,
        followUps,
        history,
        ofac: watch.map(h => watchHit(h, name)),
        deceased,
        more,
        cardTone,
        hasRed: cardTone === 'declined',
    }
}

export function buildVerificationReport(result) {
    if (!result || !result.CompanyResults) return { hasData: false }
    const business = buildBusiness(result)
    const inputs = list(result.InputEcho?.AuthorizedRepresentatives?.AuthorizedRepresentative)
    const owners = list(result.AuthorizedRepresentativeResults?.AuthorizedRepresentativeResult).filter(Boolean)
        .map((rep, i) => buildOwner(rep, i, inputs, business.links))

    const hits = [...business.ofac, ...owners.flatMap(o => o.ofac)]
    const scored = owners.filter(o => o.score != null)
    const lowest = scored.length ? scored.reduce((a, b) => (b.score < a.score ? b : a)) : null
    const unlinked = owners.filter(o => o.link?.index === 0)

    const tiles = [
        { id: 'watchlists', label: 'Watchlists', target: hits.length ? 'watchlists' : 'business',
            value: hits.length ? pieces(business.ofac.length && plural(business.ofac.length, 'business hit'),
                hits.length - business.ofac.length && plural(hits.length - business.ofac.length, 'owner hit')) : 'No hits',
            sub: hits.length ? 'Follow your internal policy on possible OFAC matches'
                : owners.length ? `Business and ${plural(owners.length, 'owner')} checked against OFAC` : 'Business checked against OFAC',
            tone: hits.length ? 'declined' : 'approved' },
        { id: 'businessIdentity', label: 'Business identity', target: 'business',
            value: scoreText(business.score), sub: `${business.verifiedCount} of 7 details verified`, tone: business.scoreTone, bar: business.score },
        lowest
            ? { id: 'ownerIdentity', label: 'Owner identity', target: lowest.key, value: scoreText(lowest.score),
                sub: lowest.capped ? 'SSN not sent, highest possible is 40 of 50' : owners.length > 1 ? `${lowest.name}, lowest of ${owners.length}` : 'Name, address, SSN and birth date checked',
                tone: lowest.tone, owners: owners.map(o => ({ name: o.firstName, score: o.score, tone: o.tone, capped: o.capped })) }
            : { id: 'ownerIdentity', label: 'Owner identity', target: 'business', value: owners.length ? 'No score' : 'No owner checked',
                sub: owners.length ? 'Lexis returned no owner score' : 'Add an owner to the request to check identities', tone: 'neutral' },
        owners.length
            ? unlinked.length
                ? { id: 'ownerLink', label: 'Owner link', target: unlinked[0].key, value: unlinked.length === owners.length && owners.length === 1
                    ? 'Cannot be linked to the business' : `${plural(unlinked.length, 'owner')} not linked`, sub: unlinked.map(o => o.name).join(', '), tone: 'watch' }
                : { id: 'ownerLink', label: 'Owner link', target: owners[0].key, value: owners[0].link?.text || 'No link returned',
                    sub: owners.length > 1 ? `All ${owners.length} owners linked` : owners[0].name, tone: owners[0].link ? 'approved' : 'neutral' }
            : { id: 'ownerLink', label: 'Owner link', target: 'business', value: 'No owner checked', sub: '', tone: 'neutral' },
        { id: 'standing', label: 'State standing', target: 'business', value: business.standing || 'No problems reported',
            sub: business.standing ? 'Confirm with the Secretary of State' : 'Lexis flags inactive or unregistered businesses', tone: business.standing ? 'declined' : 'approved' },
        { id: 'age', label: 'Business age', target: 'business', value: business.age || 'On record over 2 years',
            sub: business.age ? 'Young businesses have less history' : 'Not flagged as new by Lexis', tone: business.age.includes('12') ? 'watch' : 'approved' },
    ]

    const sentenceCase = (label) => (/^[A-Z][a-z]/.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label)
    const redNotes = (grouped, covered) => allNotes(grouped)
        .filter(n => n.tone === 'declined' && !covered.has(n.code) && !covered.has(String(Number(n.code))))
        .map(n => sentenceCase(n.label))
    const businessRedNotes = redNotes(business.notes, new Set(['12', '13', '14', '15', ...(business.ofac.length ? ['10'] : [])]))

    const issues = [
        hits.length && plural(hits.length, 'watchlist hit'),
        business.scoreTone === 'declined' && `business ${(SCORE_WORDS[business.score] || '').toLowerCase()}, ${business.score} of 50`,
        business.standing && business.standing.toLowerCase(),
        ...businessRedNotes.map(label => `business: ${label}`),
        ...owners.filter(o => o.deceased).map(o => `${o.name} reported deceased`),
        ...owners.filter(o => o.tone === 'declined').map(o => `${o.name} ${(SCORE_WORDS[o.score] || '').toLowerCase()}`),
        ...owners.flatMap(o => redNotes(o.notes, new Set(['DI', ...(o.ofac.length ? ['32'] : [])])).map(label => `${o.name}: ${label}`)),
        ...unlinked.map(o => `${o.name} cannot be linked to the business`),
    ].filter(Boolean)
    const red = hits.length > 0 || business.scoreTone === 'declined' || Boolean(business.standing) || businessRedNotes.length > 0 || owners.some(o => o.hasRed)
    const businessTones = [business.scoreTone, ...allNotes(business.notes).map(n => n.tone).filter(t => t !== 'notSent'), business.standing ? 'declined' : 'approved']
    const businessCardTone = worst(businessTones)
    const amberItems = [
        ...allNotes(business.notes).filter(n => n.tone === 'watch'),
        ...owners.flatMap(o => [...allNotes(o.notes).filter(n => n.tone === 'watch'), ...o.flags.filter(f => f.tone === 'watch' && f.label !== 'Cannot be linked')]),
    ].length
    const watchIssues = [
        business.scoreTone === 'watch' && `business ${(SCORE_WORDS[business.score] || '').toLowerCase()}, ${business.score} of 50`,
        ...owners.filter(o => o.tone === 'watch').map(o => `${o.name} ${(SCORE_WORDS[o.score] || '').toLowerCase()}, ${o.score} of 50`),
        amberItems && `${plural(amberItems, 'item')} to check`,
        business.score == null && 'Lexis returned no business score',
    ].filter(Boolean)
    const verdict = issues.length ? { text: issues.join(' · '), tone: red ? 'declined' : 'watch' }
        : watchIssues.length ? { text: `Nothing serious · ${watchIssues.join(' · ')}`, tone: 'watch' } : null
    const allClear = !verdict && businessCardTone !== 'declined' && !owners.some(o => o.cardTone === 'declined' || o.cardTone === 'watch')
        && business.scoreTone === 'approved'

    const sections = [
        ...(hits.length ? [{ id: 'watchlists', title: 'Watchlist hits', linkTitle: 'Watchlists', count: hits.length, tone: 'declined' }] : []),
        { id: 'business', title: 'Business check', linkTitle: 'Business check', count: null, tone: businessCardTone === 'declined' ? 'declined' : null },
        ...owners.map(o => ({ id: o.key, title: o.name, linkTitle: o.name, count: allNotes(o.notes).filter(n => n.tone === 'declined' || n.tone === 'watch').length || null,
            tone: o.hasRed ? 'declined' : null, owner: o })),
    ]
    sections.forEach(s => { s.open = s.tone === 'declined' && !(s.owner && owners.length >= 3 && !s.owner.hasRed) })

    return {
        hasData: true,
        business,
        owners,
        hits,
        tiles,
        verdict,
        allClear,
        sections,
        summary: {
            ownersLine: owners.length ? plural(owners.length, 'owner') + ' checked' : 'No owner checked',
        },
    }
}
